import React, { useEffect, useState } from 'react';

import { SearchVariationByString } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/SearchByName/SearchVariationByString';
import { useSearchForVariationsByUUID } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/SearchByName/useSearchForVariationsByUUID';
import { VariationDetails } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails';
import { useTrackProductSelectionEvent } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useTrackProductSelectionEvent';
import type {
  ProductCategory,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { useSearchVariationsByTermQuery } from 'src/shared/graphql/generated';

enum ActiveView {
  SEARCH = 'SEARCH',
  VIEW_VARIATION = 'VIEW_VARIATION',
}

export type FilteredProductsParams = {
  onCancel: () => void;
  category: ProductCategory;
  onApplyVariation: (v: Variation) => void;
};

export const SearchByName = ({
  onCancel,
  category,
  onApplyVariation,
}: FilteredProductsParams) => {
  const [activeView, setActiveView] = useState<ActiveView>(ActiveView.SEARCH);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [variations, setVariations] = useState<Variation[]>([]);
  const [selectedVariation, setSelectedVariation] = useState<
    Variation | undefined
  >(undefined);

  const getVariationsByUUIDs = useSearchForVariationsByUUID();
  const { data, error } = useSearchVariationsByTermQuery({
    variables: {
      searchTerm,
    },
  });

  const { trackRealProductSelectionEvent } = useTrackProductSelectionEvent({
    category,
  });

  useEffect(() => {
    if (error || !data) {
      return;
    }

    const qlVariations = data.productCatalogVariationSearch.nodes;
    const allVariations: Variation[] = getVariationsByUUIDs(
      qlVariations!.map(v => v!.id),
      category,
    );
    setVariations(allVariations);
  }, [getVariationsByUUIDs, data, error, category]);

  const actions: Record<ActiveView, Record<string, (...args: any) => void>> = {
    [ActiveView.SEARCH]: {
      onSelectVariation: (variation: Variation) => {
        setSelectedVariation(variation);
        setActiveView(ActiveView.VIEW_VARIATION);
      },
      onSearchChanged: (event: React.FormEvent<HTMLInputElement>) => {
        setSearchTerm(event.currentTarget.value);
      },
      onViewProductDetails: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${category}-search-select-click`,
          },
          variation,
        );
        setSelectedVariation(variation);
        setActiveView(ActiveView.VIEW_VARIATION);
      },
    },
    [ActiveView.VIEW_VARIATION]: {
      onBack: () => {
        setActiveView(ActiveView.SEARCH);
      },
      onDifferentSizeSelected: (variation: Variation) => {
        setSelectedVariation(variation);
      },
      onDifferentColorSelected: (variation: Variation) => {
        setSelectedVariation(variation);
      },
      onApply: () => {
        onApplyVariation(selectedVariation as Variation);
      },
    },
  };

  const viewByState: Record<ActiveView, React.ReactElement> = {
    [ActiveView.SEARCH]: (() => {
      const action = actions[ActiveView.SEARCH];
      return (
        <SearchVariationByString
          onCancel={onCancel}
          onSearchChanged={action.onSearchChanged}
          onViewProductDetails={action.onViewProductDetails}
          searchTerm={searchTerm}
          variations={variations}
        />
      );
    })(),
    [ActiveView.VIEW_VARIATION]: (
      <VariationDetails
        category={category}
        onApply={actions[ActiveView.VIEW_VARIATION].onApply}
        onBack={actions[ActiveView.VIEW_VARIATION].onBack}
        onDifferentColorSelected={
          actions[ActiveView.VIEW_VARIATION].onDifferentColorSelected
        }
        onDifferentSizeSelected={
          actions[ActiveView.VIEW_VARIATION].onDifferentSizeSelected
        }
        variation={selectedVariation as Variation}
      />
    ),
  };

  return viewByState[activeView];
};
